// Shared primitives for the bottom-panel tabs: a copy-to-clipboard button with
// transient "Copied" feedback, empty + all-clear states, a goto dispatcher that
// asks the editor to reveal a (line, col), and a few presentational helpers
// (mono copy rows, section heads). Icons are lucide-react only, never hand-
// drawn, so the panel reads like the rest of the shell.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Check, Copy, FlaskConical } from "lucide-react";

/** Ask the editor region to move the caret to (line, col). It listens for this
 *  on window; if no editor is mounted the call is simply a no-op. */
export function gotoEditor(line: number, col: number): void {
  window.dispatchEvent(new CustomEvent("seal:goto", { detail: { line, col } }));
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/** Shared copy-with-feedback hook: returns whether we recently copied + a fn to
 *  trigger a copy. The flag flips back after ~1.4s. */
function useCopy(value: string): [boolean, () => void] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const onCopy = useCallback(async () => {
    const ok = await writeClipboard(value);
    if (!ok) return;
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1400);
  }, [value]);
  return [copied, onCopy];
}

interface CopyButtonProps {
  /** The text placed on the clipboard. */
  value: string;
  /** Visible button label. When omitted the button is icon-only. */
  label?: string;
  /** Accessible label, e.g. "Copy address". */
  title: string;
}

/** A small copy button that flips to "Copied" for ~1.4s after a successful
 *  write. Pass an empty `label` for the compact icon-only variant. */
export function CopyButton({ value, label = "Copy", title }: CopyButtonProps) {
  const [copied, onCopy] = useCopy(value);
  const iconOnly = label === "";
  return (
    <button
      type="button"
      className={`pn-copy${iconOnly ? " pn-copy--icon" : ""}${copied ? " is-copied" : ""}`}
      onClick={onCopy}
      aria-label={title}
      title={copied ? "Copied" : title}
    >
      {copied ? <Check size={13} strokeWidth={2.25} /> : <Copy size={13} strokeWidth={1.9} />}
      {!iconOnly && <span>{copied ? "Copied" : label}</span>}
    </button>
  );
}

interface EmptyStateProps {
  title: string;
  hint?: ReactNode;
  icon?: ReactNode;
  /** "ok" tints the hero green for a positive all-clear (e.g. "No problems"),
   *  so every tab shares one centered hero instead of bespoke success boxes. */
  tone?: "neutral" | "ok";
}

/** The canonical centered hero shared by every tab: the "Run Compile to see …"
 *  empty state and, with tone="ok", the positive all-clear. */
export function EmptyState({ title, hint, icon, tone = "neutral" }: EmptyStateProps) {
  return (
    <div className={`pn-empty${tone === "ok" ? " pn-empty--ok" : ""}`}>
      <div className="pn-empty-ico">{icon ?? <FlaskConical size={22} strokeWidth={1.5} />}</div>
      <div className="pn-empty-title">{title}</div>
      {hint && <div className="pn-empty-hint">{hint}</div>}
    </div>
  );
}

/** The shorthand most empty states want: "Run ⌘↵ Compile to see X." */
export function runHint(noun: ReactNode): ReactNode {
  return (
    <>
      Run <kbd className="pn-kbd">⌘↵</kbd> to compile and see {noun}.
    </>
  );
}

interface SectionHeadProps {
  title: string;
  count?: ReactNode;
  right?: ReactNode;
}

/** A tiny tracked-uppercase section label with an optional trailing count and a
 *  right-aligned slot for actions. */
export function SectionHead({ title, count, right }: SectionHeadProps) {
  return (
    <div className="pn-sec-head">
      <span className="label-caps pn-sec-title">{title}</span>
      {count != null && <span className="pn-sec-count">{count}</span>}
      {right != null && <span className="pn-sec-right">{right}</span>}
    </div>
  );
}

interface KvRowProps {
  label: string;
  /** Render `value` in monospace (hashes, addresses, script). */
  mono?: boolean;
  copy?: string;
  children: ReactNode;
}

/** One row of the Output key/value grid: a tracked-caps label and a value, with
 *  an optional inline copy button for hashes. */
export function KvRow({ label, mono, copy, children }: KvRowProps) {
  return (
    <>
      <dt className="label-caps pn-kv-key">{label}</dt>
      <dd className={`pn-kv-val${mono ? " pn-kv-val--mono" : ""}`}>
        <span className="pn-kv-text">{children}</span>
        {copy != null && <CopyButton value={copy} label="" title={`Copy ${label.toLowerCase()}`} />}
      </dd>
    </>
  );
}

/** Middle-truncate a long hex/string for display: head…tail. The full value
 *  stays available via the row's copy button and title. */
export function trunc(s: string, head = 8, tail = 8): string {
  return s.length > head + tail + 1 ? `${s.slice(0, head)}…${s.slice(-tail)}` : s;
}

/** Extern declarations parsed from contract source, so the Output/Certification
 *  tabs can name which values a contract still needs bound in its args file.
 *  Matches `extern const owner: PublicKey;` and array types like
 *  `extern const keys: [PublicKey; 3];`. */
export function parseExterns(source: string): { name: string; type: string }[] {
  const out: { name: string; type: string }[] = [];
  const re = /\bextern\s+const\s+([A-Za-z_]\w*)\s*:\s*([^;=]+?)\s*;/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source))) {
    out.push({ name: m[1], type: m[2].replace(/\s+/g, " ").trim() });
  }
  return out;
}

/** Inline ASM token coloring for the compiler's disassembly format
 *  (`<hexpush> OPCODE number`): pushed data is angle-bracketed (or a bare hex
 *  blob), script numbers are bare integers, everything else is an opcode
 *  mnemonic (CHECKSIG, CSV, IF, …, with or without an OP_ prefix). */
export function AsmTokens({ asm }: { asm: string }) {
  const tokens = asm.split(/\s+/).filter(Boolean);
  return (
    <>
      {tokens.map((tok, i) => {
        const cls =
          tok.startsWith("<") || (/^(0x)?[0-9a-fA-F]{6,}$/.test(tok) && !/^OP_/i.test(tok))
            ? "pn-asm-data"
            : /^-?\d+$/.test(tok)
              ? "pn-asm-num"
              : "pn-asm-op";
        return (
          <span key={i}>
            <span className={cls}>{tok}</span>
            {i < tokens.length - 1 ? " " : ""}
          </span>
        );
      })}
    </>
  );
}

// One match = one JSON token: a string, a number, a literal, a punctuation mark,
// a whitespace run, or a stray char. Strings are classed as key vs value by
// peeking for a following colon.
const JSON_TOKEN = /"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|\b(?:true|false|null)\b|[{}[\],:]|\s+|[^\s]/g;

/** Lightweight JSON syntax coloring for the lockfile manifest, so the .lock
 *  reads like code (keys, string values, numbers, literals, punctuation) rather
 *  than a flat mono block. Colors come from the theme, so it tracks the palette. */
export function JsonHighlight({ text }: { text: string }) {
  const out: ReactNode[] = [];
  JSON_TOKEN.lastIndex = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = JSON_TOKEN.exec(text))) {
    const tok = m[0];
    let cls = "";
    if (tok[0] === '"') {
      cls = /^\s*:/.test(text.slice(JSON_TOKEN.lastIndex)) ? "pn-json-key" : "pn-json-str";
    } else if (/^-?\d/.test(tok)) {
      cls = "pn-json-num";
    } else if (tok === "true" || tok === "false" || tok === "null") {
      cls = "pn-json-lit";
    } else if (tok.length === 1 && "{}[],:".includes(tok)) {
      cls = "pn-json-punc";
    }
    out.push(
      <span key={i} className={cls || undefined}>
        {tok}
      </span>,
    );
    i += 1;
  }
  return <>{out}</>;
}
